'use client';

import classNames from 'classnames';
import { signOut } from 'next-auth/react';
import { useEffect, useMemo, useState } from 'react';
import Image from '@/components/atoms/image/image';
import RiveAnimation from '@/components/atoms/rive-animation/rive-animation';
import { OnboardingNameStepData } from '@/components/modules/onboarding-flow-name-step-form-module/onboarding-flow-name-step-form-module';
import Button from '@/components/molecules/button/button';
import ImageMarquee from '@/components/organisms/image-marquee/image-marquee';
import OnboardingCurtain from '@/components/organisms/onboarding-curtain/onboarding-curtain';
import { Module } from '@/components/utilities/content-modules/module-list/module-list';
import OnboardingFlowModuleList, {
  OnboardingFlowModule,
} from '@/components/utilities/content-modules/onboarding-flow-module-list/onboarding-flow-module-list';
import useMutationUpdateUserProfile from '@/integrations/content/users/update-user-profile/update-user-profile.hooks';
import {
  trackPageLoadEvent,
  trackViewEvent,
} from '@/integrations/tracking/google-tag-manager/scripts';
import { tealiumView } from '@/integrations/tracking/tealium/scripts';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './onboarding-flow.module.scss';

export interface OnboardingData {
  acceptedCommunication: boolean;
  acceptedMembership: boolean;
  firstName: string;
  lastName: string;
}

export interface OnboardingFlowProps {
  /** An array of the content modules to show */
  contentModules: Module[];
  /** Link to the home page */
  homePageLink?: Content.Components['LinkDto'] | null;
  /** Missing mandatory consents */
  missingMandatoryConsents?: Content.Components['MissingMandatoryConsentDto'][];
  /** Personalization data for the user */
  personalizationData?: Content.Properties.PersonalizationData;
  /** Tracking data for the page */
  trackingData?: Content.Properties.TrackingData;
}

const getVisibleOnboardingSteps = (
  steps: Content.Components['OnboardingFlowStepDto'][],
  hasLoyaltyConsent: boolean,
) => {
  if (steps.length < 2) return steps;

  const finalStepVariants = steps.slice(-2);
  const hasLoyaltyVariants =
    finalStepVariants.some(step => step.isLoyaltyStep) &&
    finalStepVariants.some(step => !step.isLoyaltyStep);

  if (!hasLoyaltyVariants) {
    return steps;
  }

  const selectedFinalStep =
    finalStepVariants.find(step => step.isLoyaltyStep === hasLoyaltyConsent) ||
    finalStepVariants[0];

  return [...steps.slice(0, -2), selectedFinalStep];
};

const OnboardingFlow = ({
  contentModules = [],
  homePageLink,
  missingMandatoryConsents,
  personalizationData,
  trackingData,
}: OnboardingFlowProps) => {
  const isTablet = useWindowSize('tablet');

  type OnboardingRootModule = {
    $type: 'OnboardingFlowModuleDto';
    steps: Content.Components['OnboardingFlowStepDto'][];
  };
  const onboardingFlowModule = (contentModules as unknown[]).find(
    (module): module is OnboardingRootModule =>
      typeof module === 'object' &&
      module !== null &&
      '$type' in module &&
      (module as { $type?: string }).$type === 'OnboardingFlowModuleDto',
  );
  const steps = useMemo(
    () => onboardingFlowModule?.steps ?? [],
    [onboardingFlowModule],
  );
  const [data, setData] = useState<OnboardingData>({
    acceptedCommunication: false,
    acceptedMembership: false,
    firstName: '',
    lastName: '',
  });
  const { mutateAsync: mutateAsyncUpdateUserProfile } =
    useMutationUpdateUserProfile();
  const [error, setError] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const visibleSteps = useMemo(
    () => getVisibleOnboardingSteps(steps, data.acceptedMembership),
    [data.acceptedMembership, steps],
  );
  const mandatoryConsentPayload = useMemo(
    () =>
      (missingMandatoryConsents ?? []).reduce<Record<string, true>>(
        (payload, consent) => {
          payload[consent.consentFlag] = true;

          return payload;
        },
        {},
      ),
    [missingMandatoryConsents],
  );
  const currentStepData = visibleSteps[stepIndex];
  const illustrationModule = currentStepData?.illustrationModule as
    | Content.Components['OnboardingFlowIllustrationModuleDto']
    | undefined;
  const illustrationImage =
    isTablet && illustrationModule?.mobileImage
      ? illustrationModule.mobileImage
      : illustrationModule?.image;
  const illustrationImageDimensions = illustrationImage?.dimensions;

  const currentStep = stepIndex + 1;
  const totalSteps = visibleSteps.length;

  const goToNextStep = () => {
    setStepIndex(previousStepIndex =>
      Math.min(previousStepIndex + 1, visibleSteps.length - 1),
    );
  };

  const goToPreviousStep = () => {
    setError(undefined);
    setStepIndex(previousStepIndex => Math.max(previousStepIndex - 1, 0));
  };

  const handleOnClickBack = () => {
    if (stepIndex > 0) {
      goToPreviousStep();

      return;
    }

    signOut({ callbackUrl: homePageLink?.url ?? '/' });
  };

  const handleNameStepComplete = (values: OnboardingNameStepData) => {
    setData(previousData => ({
      ...previousData,
      acceptedCommunication: values.acceptedCommunication,
      firstName: values.firstName,
      lastName: values.lastName,
    }));
    goToNextStep();
  };

  const handleMembershipStepComplete = async (acceptedMembership: boolean) => {
    const updatedData = { ...data, acceptedMembership };

    setData(updatedData);
    setIsSubmitting(true);
    setError(undefined);

    try {
      if (!visibleSteps.length) {
        throw new Error('No onboarding steps available.');
      }

      const updateUserProfilePayload = {
        firstName: updatedData.firstName,
        hasLoyaltyConsent: updatedData.acceptedMembership,
        hasMarketingConsent: updatedData.acceptedCommunication,
        lastName: updatedData.lastName,
        ...mandatoryConsentPayload,
      } as Partial<Content.Requests.UpdateUserProfileRequestBody>;

      await mutateAsyncUpdateUserProfile({
        data: updateUserProfilePayload,
        refreshSession: true,
      });

      const nextVisibleSteps = getVisibleOnboardingSteps(
        steps,
        updatedData.acceptedMembership,
      );
      const nextStepIndex = Math.min(
        stepIndex + 1,
        nextVisibleSteps.length - 1,
      );

      setStepIndex(nextStepIndex);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.';

      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    trackPageLoadEvent({ content_group: 'onboarding_flow' });

    personalizationData && tealiumView(personalizationData);

    if (!trackingData?.gtmDataLayer) return;

    trackViewEvent(trackingData.gtmDataLayer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!currentStepData) return null;

  return (
    <div className={styles.component}>
      <nav className={styles.navigation}>
        <Button
          icon="ArrowLeft"
          iconPosition="left"
          onClick={handleOnClickBack}
          variant="ghost"
        />
      </nav>

      <section className={styles.left}>
        <div className={styles.topBanner} />

        {illustrationModule?.showBackground && (
          <ImageMarquee
            className={styles.logoMarquee}
            gap={!isTablet ? 22 : undefined}
            imageSize={!isTablet ? 55 : undefined}
          />
        )}

        {illustrationModule?.showConfetti && (
          <div className={styles.confetti}>
            <RiveAnimation
              height={!isTablet ? 500 : 1000}
              name="Confetti"
              width={!isTablet ? 500 : 1000}
            />
          </div>
        )}

        {illustrationImage?.url && illustrationImageDimensions?.width && (
          <Image
            alt={illustrationImage.altText || illustrationImage.name}
            className={classNames(styles.leftImage, {
              [styles.leftImageWithBackground]:
                illustrationModule?.showBackground,
              [styles.leftImageWithoutBackground]:
                !illustrationModule?.showBackground,
            })}
            focalPoint={illustrationImage.focalPoint}
            height={illustrationImageDimensions.height}
            priority
            src={illustrationImage.url}
            width={illustrationImageDimensions.width}
          />
        )}
      </section>

      <section className={styles.right}>
        <div className={styles.rightInner}>
          <OnboardingCurtain
            currentStep={currentStep}
            error={error}
            totalSteps={totalSteps}
          >
            <OnboardingFlowModuleList
              isSubmitting={isSubmitting}
              modules={currentStepData.contentModules as OnboardingFlowModule[]}
              onMembershipStepComplete={handleMembershipStepComplete}
              onNameStepComplete={handleNameStepComplete}
            />
          </OnboardingCurtain>
        </div>
      </section>
    </div>
  );
};

OnboardingFlow.displayName = 'OnboardingFlow';

export default OnboardingFlow;
